import { useLayoutEffect, useMemo, useState } from 'react';
import { createGlobalStyle, css, StyleSheetManager } from 'styled-components';
import { useMountingInventoryAPI } from '@elfsight/widget-mounting-inventory';
import { parseCustomCSS } from './parsed-custom-css';
import { prefixCSSRules } from './prefix-css-rules';

export const createCustomCSSPrefixClassName = (
  appAlias: string,
  widgetId?: string
) => ['eapps', appAlias, widgetId, 'custom-css-root'].filter(Boolean).join('-');

export type CustomCSSProps = {
  customCSS: string;
  prefixClassName: string;
};

export function CustomCSS({ customCSS = '', prefixClassName }: CustomCSSProps) {
  const mountingInventoryAPI = useMountingInventoryAPI();

  const { external, internal } = useMemo(
    () =>
      parseCustomCSS(customCSS, {
        widgetRootSelector: mountingInventoryAPI?.getWidgetRootSelector()
      }),
    [customCSS, mountingInventoryAPI]
  );

  const [finalInternalCSS, setFinalInternalCSS] = useState('');

  const tempStyleElement = useMemo(() => {
    const styleElement = document.createElement('style');
    const tmpDocument = document.implementation.createHTMLDocument();
    return tmpDocument.body.insertBefore(styleElement, null);
  }, []);

  useLayoutEffect(() => {
    tempStyleElement.innerHTML = internal;

    if (tempStyleElement?.sheet) {
      const prefixedRules = prefixCSSRules(
        tempStyleElement.sheet.cssRules,
        `.${prefixClassName}`
      );

      setFinalInternalCSS(prefixedRules);
    }
  }, [tempStyleElement, internal]);

  const finalCSS = `${external}\n${finalInternalCSS}`;

  return (
    <StyleSheetManager disableCSSOMInjection>
      <Styles $css={finalCSS} />
    </StyleSheetManager>
  );
}
const Styles = createGlobalStyle<{
  $css: string | undefined;
}>`${({ $css }) => css`
  ${$css}
`}`;
